import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Disc3, Wrench, Shuffle } from "lucide-react";

const MODES = [
  { id: "release", label: "Release promo", Icon: Disc3,
    desc: "Caption + video centered on your latest Spotify drop." },
  { id: "production", label: "Music production", Icon: Wrench,
    desc: "Studio, tips, BTS. Great for staying relevant between releases." },
  { id: "mixed", label: "Mixed", Icon: Shuffle,
    desc: "A blend of both. Recommended default for a full week." },
];

export default function GenerateDialog({ open, onOpenChange, onStarted }) {
  const [mode, setMode] = useState("mixed");
  const [count, setCount] = useState(5);
  const [submitting, setSubmitting] = useState(false);

  const run = async () => {
    setSubmitting(true);
    try {
      const res = await api.post("/drafts/generate", { mode, count });
      toast(`Generating ${res.data.total} drafts (${res.data.mode})…`);
      onOpenChange(false);
      onStarted?.(res.data);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Failed to start generation";
      toast.error(typeof detail === "string" ? detail : "Failed to start generation");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0A0A0A] border-white/10 rounded-sm w-[calc(100vw-1rem)] max-w-xl text-white max-h-[92vh] overflow-y-auto p-4 sm:p-6"
        data-testid="generate-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-3xl tracking-tight flex items-center gap-3">
            <Sparkles size={18} className="text-[#FF3B00]" /> Generate batch
          </DialogTitle>
        </DialogHeader>
        <p className="text-white/55 text-sm mt-2 mb-6">
          What kind of week are we cooking up?
        </p>

        <div className="space-y-3 mb-6">
          {MODES.map(({ id, label, Icon, desc }) => (
            <button
              key={id}
              type="button"
              onClick={() => setMode(id)}
              data-testid={`gen-mode-${id}`}
              className={`w-full text-left p-4 border flex gap-4 items-start transition-colors ${
                mode === id
                  ? "border-[#FF3B00] bg-[#FF3B00]/5"
                  : "border-white/10 hover:border-white/25"
              }`}
            >
              <Icon size={18} strokeWidth={1.4} className={mode === id ? "text-[#FF3B00]" : "text-white/60"} />
              <div>
                <div className="font-sans text-base">{label}</div>
                <div className="text-xs text-white/50 mt-1">{desc}</div>
              </div>
            </button>
          ))}
        </div>

        <div className="flex items-center justify-between mb-6">
          <div className="label-mono">Posts in batch</div>
          <div className="flex items-center gap-2">
            {[3, 5, 7].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setCount(n)}
                data-testid={`gen-count-${n}`}
                className={`w-10 h-10 border font-mono text-sm transition-colors ${
                  count === n
                    ? "border-[#FF3B00] bg-[#FF3B00]/10 text-white"
                    : "border-white/10 text-white/60 hover:text-white"
                }`}
              >
                {n}
              </button>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
          <button
            className="btn-ghost-line"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="gen-dialog-cancel"
          >
            Cancel
          </button>
          <button
            className="btn-amber disabled:opacity-60"
            onClick={run}
            disabled={submitting}
            data-testid="gen-dialog-start"
          >
            {submitting ? "Starting…" : "Start generation"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
